import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Crown, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = ({ onUpgrade, inApp = false, compact = false }) => {
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const navigate = useNavigate();

  const features = [
    { name: 'Personalized Daily Affirmations', free: '1 per day', growth: 'Unlimited' },
    { name: 'Journaling', free: '1 AI insight per week', growth: 'Unlimited AI insights' },
    { name: 'AI Companion (Conchi)', free: '5 messages per day', growth: 'Unlimited conversations' },
    { name: 'Love Garden Game', free: 'Full access', growth: 'Full access' },
    { name: 'Community', free: 'Full access', growth: 'Full access' },
    { name: 'Divine Guidance (Astrology)', free: '1 reading per week', growth: 'Unlimited readings' },
    { name: 'Vision Board', free: '1 board maximum', growth: 'Unlimited boards' },
    { name: 'Love Vault (Save Affirmations)', free: '10 saves per month', growth: 'Unlimited saves' },
    { name: 'Priority AI response times', free: false, growth: true },
    { name: 'Advanced mood analytics', free: false, growth: true },
    { name: 'Exclusive content & early access', free: false, growth: true },
  ];

  const handleCTA = (plan) => {
    if (onUpgrade) {
      onUpgrade(plan, billingPeriod);
    } else {
      // Redirect to signup with navigate
      navigate('/signup');
    }
  };

  return (
    <div className={`${inApp ? 'py-8' : 'py-20'} bg-gradient-to-br from-rose-50 via-white to-rose-100`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Free Trial Notice */}
        {!compact && (
          <div className="bg-gradient-to-r from-yellow-100 via-yellow-50 to-yellow-100 border-2 border-yellow-400 rounded-2xl p-6 mb-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Zap className="w-8 h-8 text-yellow-600" />
              <h3 className="text-2xl font-bold text-gray-900">
                Everyone Starts with 7-Day Growth Trial
              </h3>
            </div>
            <p className="text-center text-gray-700 text-lg">
              All new users get <span className="font-bold text-rose-600">full access to Growth features for 7 days free</span>. 
              No credit card required. After the trial, choose to stay on Growth or switch to the free Reflect plan.
            </p>
          </div>
        )}

        {/* Header */}
        {!compact && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-rose-500 to-rose-600 bg-clip-text text-transparent">
                Choose Your Path After Trial
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience everything first with your free trial, then select the plan that fits your journey.
            </p>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:text-gray-900 border-2 border-gray-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={`relative px-6 py-3 rounded-full font-semibold transition-all ${
              billingPeriod === 'annual'
                ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:text-gray-900 border-2 border-gray-200'
            }`}
          >
            Annual
            <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
              Save $34
            </span>
          </button>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* Reflect Plan (Free) */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-200 relative">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Reflect</h3>
              <p className="text-gray-600 mb-4">Perfect for getting started</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/ forever</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">1 daily affirmation</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">1 AI journal insight per week</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">5 Conchi messages per day</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Full Love Garden & Community</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">1 Divine Guidance per week</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">1 Vision Board maximum</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">10 Love Vault saves per month</span>
              </li>
            </ul>

            <Button
              onClick={() => handleCTA('reflect')}
              className="w-full py-6 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-full"
            >
              Start 7-Day Growth Trial →
            </Button>
            <p className="text-gray-500 text-xs text-center mt-3">
              All signups start with Growth trial, then choose your plan
            </p>
          </div>

          {/* Growth Plan (Paid) - MOST POPULAR */}
          <div className="bg-gradient-to-br from-rose-400 to-rose-600 rounded-3xl shadow-2xl p-8 relative transform md:scale-105">
            {/* Most Popular Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-yellow-400 text-yellow-900 px-6 py-2 rounded-full font-bold text-sm shadow-lg flex items-center gap-2">
                <Crown className="w-4 h-4" />
                MOST POPULAR
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                Growth
                <Sparkles className="w-6 h-6 text-yellow-300" />
              </h3>
              <p className="text-rose-100 mb-4">Unlock your full potential</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-white">
                  ${billingPeriod === 'monthly' ? '17' : '169'}
                </span>
                <span className="text-rose-100">
                  / {billingPeriod === 'monthly' ? 'month' : 'year'}
                </span>
              </div>
              {billingPeriod === 'annual' && (
                <p className="text-yellow-300 text-sm font-semibold mt-2">
                  Just $14.08/month - Save 2 months!
                </p>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-6">
              <p className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-300" />
                7-Day Free Trial Included
              </p>
              <p className="text-rose-100 text-xs">
                No card required. Experience everything before you decide.
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited daily affirmations</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited AI journal insights</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited Conchi conversations</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Full Love Garden & Community</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited Divine Guidance</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited Vision Boards</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Unlimited Love Vault saves</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Priority AI response times</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span className="text-white font-semibold">Advanced mood analytics</span>
              </li>
            </ul>

            <Button
              onClick={() => handleCTA('growth')}
              className="w-full py-6 bg-white hover:bg-gray-100 text-rose-600 font-bold rounded-full shadow-xl text-lg"
            >
              {inApp ? 'Upgrade to Growth →' : 'Start 7-Day Free Trial →'}
            </Button>

            <p className="text-rose-100 text-xs text-center mt-4">
              Cancel anytime. No questions asked. 💕
            </p>
          </div>
        </div>

        {/* Feature Comparison Table (Optional - for desktop) */}
        {!compact && (
          <div className="hidden lg:block bg-white rounded-3xl shadow-xl p-8 max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-8">
              Complete Feature Comparison
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 text-gray-700 font-semibold">Feature</th>
                    <th className="text-center py-4 px-4 text-gray-700 font-semibold">Reflect (Free)</th>
                    <th className="text-center py-4 px-4 text-rose-600 font-semibold">Growth (Paid)</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4 text-gray-700">{feature.name}</td>
                      <td className="text-center py-4 px-4">
                        {typeof feature.free === 'boolean' ? (
                          feature.free ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className="text-sm text-gray-600">{feature.free}</span>
                        )}
                      </td>
                      <td className="text-center py-4 px-4">
                        {typeof feature.growth === 'boolean' ? (
                          feature.growth ? (
                            <Check className="w-5 h-5 text-rose-600 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )
                        ) : (
                          <span className="text-sm font-semibold text-rose-600">{feature.growth}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trust Signals */}
        {!compact && (
          <div className="mt-12 text-center">
            <div className="flex items-center justify-center gap-8 flex-wrap text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>No contracts</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>Secure payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>24/7 support</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingSection;
